import { AlertTriangle, LockKeyhole, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'

import type {
  LedgerGlAccountNode,
  ManualJournalLineRequest,
  PostedJournalResult,
} from '@/api/ledger-types'
import { session } from '@/api/session'
import { ProblemAlert } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { MoneyInput } from '@/components/money-input'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { branchLabel } from '@/lib/branch'
import { formatBusinessDate } from '@/lib/datetime'
import type { Pesewas } from '@/lib/money'

import { BranchSelect } from './branch-reach'
import { today } from './constants'
import { LedgerDualControlNotice, LedgerDualControlOutcome } from './dual-control'
import { usePostManualJournal } from './mutations'
import { LEDGER_PERMISSIONS, useLedgerPermissions } from './permissions'
import { useBranchBusinessDay, useChartOfAccounts, useLedgerBranchReach } from './queries'

/**
 * Raising a journal by hand.
 *
 * The most abusable single operation in a core banking system. Every other posting in this
 * application is the by-product of something that happened — a customer handed over cash, a loan
 * was disbursed, an accrual ran — and can be traced back to it. A manual journal is a movement
 * between any two accounts on one person's say-so, and the controls around it are the reason the
 * ledger can be trusted at all:
 *
 * - **It is countersigned.** Confirming may only queue it. The screen reads the envelope and says
 *   which happened rather than reporting "posted" and leaving the maker to find out otherwise.
 * - **It must balance.** Debits equal credits to the pesewa, checked here before the request is
 *   built and again by the server, which refuses with `ledger.unbalanced_journal`.
 * - **It refuses a narrow screen.** `docs/mobile-policy.md` — money is keyed here, and this is the
 *   screen the policy's "manual journal is the highest-privilege write in the system; it needs the
 *   whole picture visible at once" line was written about.
 *
 * **Money never leaves integer pesewas.** Each line's amount comes out of {@link MoneyInput} as an
 * integer count of minor units, is compared and totalled as integers, and travels to the API as an
 * integer. Nothing on this screen multiplies a decimal by a hundred.
 */

interface DraftLine {
  /** Stable across re-renders and re-orderings, so a keyed amount never jumps between rows. */
  key: string
  glAccountCode: string
  side: 'Debit' | 'Credit'
  /** Integer pesewas, or null while the field is empty. Empty is not zero. */
  amountMinorUnits: Pesewas | null
  narration: string
}

let nextLineKey = 0
const newLine = (): DraftLine => ({
  key: `line-${String(nextLineKey++)}`,
  glAccountCode: '',
  side: 'Debit',
  amountMinorUnits: null,
  narration: '',
})

/** The chart arrives as a tree; a picker needs it flat. Postable leaves only — headings refuse. */
function flattenPostable(nodes: LedgerGlAccountNode[]): LedgerGlAccountNode[] {
  return nodes.flatMap((node) => [
    ...(node.isPostable && node.status === 'Active' ? [node] : []),
    ...flattenPostable(node.children),
  ])
}

export function ManualJournalSection() {
  const permissions = useLedgerPermissions()
  const reach = useLedgerBranchReach()

  const [chosenBranchId, setChosenBranchId] = useState<string | null>(null)
  const [valueDate, setValueDate] = useState(today)
  const [description, setDescription] = useState('')
  const [lines, setLines] = useState<DraftLine[]>(() => [newLine(), newLine()])
  const [result, setResult] = useState<PostedJournalResult | null>(null)

  const post = usePostManualJournal()

  const branchId = chosenBranchId ?? session.getBranchId() ?? reach.homeBranchId
  const selected = reach.branches.find((branch) => branch.id === branchId) ?? null
  const day = useBranchBusinessDay(selected?.id ?? '')

  const chart = useChartOfAccounts({ postableOnly: true }, permissions.canViewAccounts)
  const postable = chart.data ? flattenPostable(chart.data.roots) : []

  if (!permissions.canPostManualJournal) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LockKeyhole className="size-4 text-muted" aria-hidden />
            Raising a journal by hand
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-body">
            A manual journal needs{' '}
            <span className="font-mono text-xs">{LEDGER_PERMISSIONS.journalManual}</span>, which your
            role does not hold. It is the highest-privilege write in the system and is held by very
            few people by design — every other posting in the ledger is the by-product of something
            that happened, and this one is not.
          </p>
        </CardContent>
      </Card>
    )
  }

  /*
   * Totals in integer pesewas. A line with an empty amount contributes nothing rather than zero,
   * which is the same number here and a different fact: an empty line is incomplete, and the
   * submit check below refuses it.
   */
  const debitMinorUnits = lines
    .filter((line) => line.side === 'Debit')
    .reduce((total, line) => total + (line.amountMinorUnits ?? 0), 0)
  const creditMinorUnits = lines
    .filter((line) => line.side === 'Credit')
    .reduce((total, line) => total + (line.amountMinorUnits ?? 0), 0)
  const differenceMinorUnits = debitMinorUnits - creditMinorUnits

  const complete = lines.every(
    (line) =>
      line.glAccountCode.trim() !== '' &&
      line.amountMinorUnits !== null &&
      line.amountMinorUnits > 0,
  )
  const balanced = differenceMinorUnits === 0 && debitMinorUnits > 0
  const hasBothSides =
    lines.some((line) => line.side === 'Debit') && lines.some((line) => line.side === 'Credit')

  const unknownAccounts =
    postable.length === 0
      ? []
      : lines
          .map((line) => line.glAccountCode.trim())
          .filter(
            (code) => code !== '' && !postable.some((account) => account.code === code),
          )

  const submittable =
    selected !== null &&
    description.trim() !== '' &&
    valueDate !== '' &&
    lines.length >= 2 &&
    complete &&
    balanced &&
    hasBothSides &&
    unknownAccounts.length === 0 &&
    !post.isPending

  const updateLine = (key: string, patch: Partial<DraftLine>) => {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)))
    setResult(null)
    post.reset()
  }

  return (
    <RequiresWiderScreen
      screen="Raising a journal by hand"
      reason="Amounts are keyed here, and a manual journal moves money between any two accounts on your own authority. It needs the whole entry — every line, both totals, and whether they agree — visible at once."
    >
      <div className="space-y-4">
        <LedgerDualControlNotice
          action="Raising a journal by hand"
          approvePermission={LEDGER_PERMISSIONS.journalManualApprove}
          canCountersign={permissions.canCountersignManualJournal}
          expiry="A ledger journal request expires twelve hours after it is made — half the institution's usual window — so one raised late in the afternoon lapses before the next working afternoon."
        />

        <Card>
          <CardHeader>
            <div className="min-w-0">
              <CardTitle>The entry</CardTitle>
              <p className="mt-0.5 text-xs text-muted">
                Where it posts, when it takes effect, and what it is for.
              </p>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="grid gap-4 lg:grid-cols-3">
              <BranchSelect
                id="journal-branch"
                label="Branch"
                branches={reach.branches}
                value={selected?.id ?? null}
                onChange={(next) => {
                  setChosenBranchId(next)
                  setResult(null)
                  post.reset()
                }}
                description="Only branches the server has confirmed you may act in are offered."
              />

              <div className="space-y-1.5">
                <Label htmlFor="journal-value-date">Value date</Label>
                <Input
                  id="journal-value-date"
                  type="date"
                  value={valueDate}
                  onChange={(event) => {
                    setValueDate(event.target.value)
                    setResult(null)
                    post.reset()
                  }}
                  aria-describedby="journal-value-date-help"
                />
                <p id="journal-value-date-help" className="text-xs text-muted">
                  {day.data?.businessDate
                    ? `The entry posts into the branch's open day, ${formatBusinessDate(day.data.businessDate)}. The value date is what it is effective from and may differ.`
                    : 'The entry posts into the branch’s open day. The value date is what it is effective from and may differ.'}
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="journal-description">Description</Label>
                <Input
                  id="journal-description"
                  value={description}
                  autoComplete="off"
                  placeholder="What this entry is for"
                  onChange={(event) => {
                    setDescription(event.target.value)
                    setResult(null)
                    post.reset()
                  }}
                  aria-describedby="journal-description-help"
                />
                <p id="journal-description-help" className="text-xs text-muted">
                  What a checker reads before signing, and what appears in every enquiry afterwards.
                </p>
              </div>
            </div>

            {day.data && !day.data.acceptsPostings ? (
              <Alert tone="warning">
                <AlertTriangle aria-hidden />
                <div className="space-y-1">
                  <AlertTitle>This branch is not accepting postings</AlertTitle>
                  <AlertDescription>
                    {branchLabel(selected)} has no open day, or its day is closing. The server will
                    refuse this entry. Open the day first on the business-days screen.
                  </AlertDescription>
                </div>
              </Alert>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="min-w-0">
              <CardTitle>Lines</CardTitle>
              <p className="mt-0.5 text-xs text-muted">
                Every line is a debit or a credit against one postable account. Headings refuse a
                posting.
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setLines((current) => [...current, newLine()])
                setResult(null)
                post.reset()
              }}
            >
              <Plus />
              Add a line
            </Button>
          </CardHeader>

          <CardContent className="space-y-3">
            {!permissions.canViewAccounts ? (
              <Alert tone="warning">
                <AlertTriangle aria-hidden />
                <div className="space-y-1">
                  <AlertTitle>The chart of accounts is not readable by your role</AlertTitle>
                  <AlertDescription>
                    Reading it needs{' '}
                    <span className="font-mono text-xs">{LEDGER_PERMISSIONS.accountView}</span>, so
                    account codes have to be typed from memory and cannot be checked here before the
                    entry is sent. The server still refuses a heading or an unknown code.
                  </AlertDescription>
                </div>
              </Alert>
            ) : null}

            <datalist id="postable-accounts">
              {postable.map((account) => (
                <option key={account.publicId} value={account.code}>
                  {account.name}
                </option>
              ))}
            </datalist>

            <ul className="space-y-3">
              {lines.map((line, index) => {
                const account = postable.find((candidate) => candidate.code === line.glAccountCode.trim())
                const unknown =
                  postable.length > 0 && line.glAccountCode.trim() !== '' && account === undefined

                return (
                  <li
                    key={line.key}
                    className="grid gap-3 rounded-card border border-border-subtle bg-surface-muted p-3 lg:grid-cols-[1fr_9rem_12rem_1fr_auto]"
                  >
                    <div className="space-y-1.5">
                      <Label htmlFor={`${line.key}-account`}>Account</Label>
                      <Input
                        id={`${line.key}-account`}
                        list="postable-accounts"
                        value={line.glAccountCode}
                        autoComplete="off"
                        spellCheck={false}
                        className="font-mono"
                        aria-invalid={unknown || undefined}
                        onChange={(event) => updateLine(line.key, { glAccountCode: event.target.value })}
                      />
                      <p className="text-2xs text-muted">
                        {unknown ? (
                          <span className="text-danger-text">
                            No postable account has that code. Only the leaves of the chart take
                            entries.
                          </span>
                        ) : (
                          (account?.name ?? 'Type or choose a postable account code.')
                        )}
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor={`${line.key}-side`}>Side</Label>
                      <Select
                        value={line.side}
                        onValueChange={(next) =>
                          updateLine(line.key, { side: next === 'Credit' ? 'Credit' : 'Debit' })
                        }
                      >
                        <SelectTrigger id={`${line.key}-side`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Debit">Debit</SelectItem>
                          <SelectItem value="Credit">Credit</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor={`${line.key}-amount`}>Amount</Label>
                      <MoneyInput
                        id={`${line.key}-amount`}
                        value={line.amountMinorUnits}
                        onChange={(amountMinorUnits) => updateLine(line.key, { amountMinorUnits })}
                      />
                      <p className="text-2xs text-muted">
                        {line.amountMinorUnits !== null && line.amountMinorUnits <= 0
                          ? 'A line must carry an amount above zero.'
                          : 'Keyed in cedis and pesewas; held as pesewas.'}
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor={`${line.key}-narration`}>Narration (optional)</Label>
                      <Input
                        id={`${line.key}-narration`}
                        value={line.narration}
                        autoComplete="off"
                        onChange={(event) => updateLine(line.key, { narration: event.target.value })}
                      />
                    </div>

                    <div className="flex items-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Remove line ${String(index + 1)}`}
                        disabled={lines.length <= 2}
                        onClick={() => {
                          setLines((current) => current.filter((candidate) => candidate.key !== line.key))
                          setResult(null)
                          post.reset()
                        }}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </li>
                )
              })}
            </ul>

            <div className="grid gap-3 rounded-card border border-border-subtle bg-surface p-3 sm:grid-cols-3">
              <Total label="Debits" value={debitMinorUnits} />
              <Total label="Credits" value={creditMinorUnits} />
              <div className="min-w-0">
                <p className="text-2xs font-semibold tracking-wide text-muted uppercase">Difference</p>
                <MoneyDisplay
                  value={differenceMinorUnits}
                  signed
                  colourNegative
                  emphasis="strong"
                  className="mt-0.5"
                />
                <p className="mt-0.5 text-2xs text-muted">
                  {balanced
                    ? 'Balanced to the pesewa.'
                    : 'Debits and credits must be equal before this can be sent.'}
                </p>
              </div>
            </div>

            {post.isError ? <ProblemAlert error={post.error} /> : null}

            {result ? (
              <LedgerDualControlOutcome
                result={result}
                awaitingDescription={
                  <>
                    The journal is stored and waiting for a countersignature.{' '}
                    <strong>Nothing has posted</strong> — there is no entry number yet and no
                    movement in any account. Do not reconcile against it.
                  </>
                }
                appliedDescription={
                  <>
                    The journal posted
                    {result.entryNumber ? (
                      <>
                        {' '}
                        as <span className="font-mono text-xs">{result.entryNumber}</span>
                      </>
                    ) : null}
                    {result.businessDate ? <> on {formatBusinessDate(result.businessDate)}</> : null}
                    . It is immutable from here: the only correction is a reversal.
                  </>
                }
              />
            ) : null}

            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="primary"
                disabled={!submittable}
                onClick={() => {
                  if (selected === null) {
                    return
                  }

                  const payload: ManualJournalLineRequest[] = lines.map((line) => ({
                    glAccountCode: line.glAccountCode.trim(),
                    // The side decides which member carries the amount; the other is a real zero.
                    // A line is a debit or a credit, never both, and never neither.
                    debitMinorUnits: line.side === 'Debit' ? (line.amountMinorUnits ?? 0) : 0,
                    creditMinorUnits: line.side === 'Credit' ? (line.amountMinorUnits ?? 0) : 0,
                    narration: line.narration.trim() === '' ? null : line.narration.trim(),
                  }))

                  post.mutate(
                    {
                      branchId: Number(selected.id),
                      valueDate,
                      description: description.trim(),
                      lines: payload,
                    },
                    { onSuccess: setResult },
                  )
                }}
              >
                {post.isPending ? 'Sending…' : 'Send the journal'}
              </Button>

              {!submittable && result === null ? (
                <p className="text-xs text-muted">
                  {!hasBothSides
                    ? 'An entry needs at least one debit and one credit.'
                    : !complete
                      ? 'Every line needs an account and an amount above zero.'
                      : unknownAccounts.length > 0
                        ? 'One of the account codes is not a postable account.'
                        : !balanced
                          ? 'Debits and credits do not agree.'
                          : description.trim() === ''
                            ? 'The entry needs a description.'
                            : 'Choose a branch to post into.'}
                </p>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>
    </RequiresWiderScreen>
  )
}

function Total({ label, value }: { label: string; value: Pesewas }) {
  return (
    <div className="min-w-0">
      <p className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</p>
      <MoneyDisplay value={value} emphasis="strong" className="mt-0.5" />
    </div>
  )
}
